import { create } from 'qrcode';

/**
 * A real, scannable QR code as a single inline <svg>.
 *
 * Uses `qrcode`'s SYNCHRONOUS `create()` (which returns the module bitmap) and
 * draws the path here, rather than its async `toString()`/`toDataURL()` helpers.
 * That is load-bearing: `PassportDocument.tsx` is rendered by
 * `renderToStaticMarkup`, which cannot await anything, and the passport PDF
 * must be a self-contained document with zero network references — an inline
 * path satisfies `assertSelfContainedHtml`, an <img src> to a QR service would
 * both break determinism and leak every scan to a third party.
 *
 * Error-correction level M (~15% recovery) is the usual print/screen default:
 * L is fragile against a scuffed printout, and H inflates the module count
 * enough to hurt scanning at the sizes we render.
 */
const ERROR_CORRECTION_LEVEL = 'M';

/** Quiet zone in modules. The QR spec requires 4; scanners genuinely fail
 * without it, and it is the most common cause of a "valid" code that no phone
 * will lock onto. Do not trim this to save space. */
const QUIET_ZONE = 4;

/**
 * Builds the SVG path data for `value` plus the viewBox extent, both in module
 * units. Exported for tests and for callers that need the raw geometry.
 *
 * One `<path>` for every dark module rather than a `<rect>` each: same pixels,
 * a fraction of the DOM, and it keeps the PDF's HTML small enough not to
 * matter next to the rest of the document.
 *
 * @pure
 */
export function qrPath(value: string): { path: string; extent: number } {
  const { modules } = create(value, { errorCorrectionLevel: ERROR_CORRECTION_LEVEL });
  const size = modules.size;
  const data = modules.data;

  let path = '';
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (data[y * size + x]) {
        path += `M${x + QUIET_ZONE},${y + QUIET_ZONE}h1v1h-1z`;
      }
    }
  }
  return { path, extent: size + QUIET_ZONE * 2 };
}

/**
 * `size` is the rendered edge in CSS pixels. Below roughly 90px a phone
 * struggles to resolve the modules of a URL-length code, so callers that want
 * a decorative mark should use an icon, not a QR nobody can scan.
 *
 * Colors are hardcoded black-on-white rather than themed: a QR code is read by
 * a camera, not a person, and inverting it in dark mode breaks scanners that
 * assume dark-on-light. The white ground is the quiet zone and must stay
 * opaque.
 */
export function QrCode({
  value,
  size = 160,
  title,
}: {
  value: string;
  size?: number;
  /** Accessible name. Screen readers cannot use the code itself, so say what
   * it points at; sighted users get the URL as selectable text alongside. */
  title: string;
}) {
  const { path, extent } = qrPath(value);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      role="img"
      aria-label={title}
      shapeRendering="crispEdges"
    >
      <rect width={extent} height={extent} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
